<template>
    <div class="phu-config">
        <ConfigNav :sections="sections" :active="activeSection" @select="scrollTo" @back="requestClose" />

        <div class="phu-config__main">
            <EquipmentSection ref="equipment" :groups="groups" @change-count="changeCount" />

            <HoldingSection
                ref="holding"
                :workstations="workstations"
                :phus="phus"
                :products="products"
                :layouts="layouts"
                :min-slots="minSlots"
                @rename="renamePhu"
                @move="movePhu"
                @remove="confirmRemovePhu"
                @select-layout="changeLayout"
                @edit-layout="editLayout"
                @add="addPhu"
                @select-slot="selectSlot"
                @clear-slot="clearSlot"
            />

            <ProductsSection ref="products" :products="productList" @toggle="toggleProduct" @container="setContainer" />

            <footer class="phu-config__actions">
                <button type="button" class="phu-config__cancel" @click="requestClose">{{ $t('phu_production.common.cancel') }}</button>
                <button type="button" class="phu-config__save" @click="save">{{ $t('phu_production.common.save') }}</button>
            </footer>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import ConfigNav from './ConfigNav.vue'
import EquipmentSection from './EquipmentSection.vue'
import HoldingSection from './HoldingSection.vue'
import ProductsSection from './ProductsSection.vue'
import { EQUIPMENT_STATUS, layoutSize } from '@/store/static/phuProduction'
import { getUnderAssignedProducts } from '../utils/validation'

const emptyRuntime = () => ({
    units: 0,
    batch_id: null,
    started_at: null,
    ready_at: null,
    expires_at: null
})

export default {
    name: 'PhuConfiguration',
    components: { ConfigNav, EquipmentSection, HoldingSection, ProductsSection },
    data() {
        return {
            activeSection: 'equipment',
            products: {},
            phus: [],
            groups: [],
            savedSnapshot: ''
        }
    },
    computed: {
        workstations() {
            return this.$store.getters['phuProduction/getWorkstations']
        },
        minSlots() {
            return this.$store.getters['phuProduction/getMinSlotsPerProduct']
        },
        layouts() {
            return this.$store.getters['phuProduction/getLayoutTemplates']
        },
        productList() {
            return Object.values(this.products)
        },
        sections() {
            return [
                { id: 'equipment', label: this.$t('phu_production.configuration.equipment'), count: this.groups.length },
                { id: 'holding', label: this.$t('phu_production.configuration.holding'), count: this.phus.length },
                { id: 'products', label: this.$t('phu_production.configuration.products'), count: this.productList.length }
            ]
        }
    },
    methods: {
        scrollTo(sectionId) {
            this.activeSection = sectionId
            const section = this.$refs[sectionId]
            if (section) {
                section.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        },
        configurationSnapshot() {
            return JSON.stringify({
                products: this.products,
                phus: this.phus.map((phu) => {
                    const copy = { ...phu }
                    delete copy.choosing_layout
                    return copy
                }),
                groups: this.groups.map((group) => ({ key: group.key, count: group.count }))
            })
        },
        requestClose() {
            if (this.configurationSnapshot() === this.savedSnapshot) {
                this.close()
                return
            }
            this.$popup.phuNoEquipment({
                title: this.$t('phu_production.configuration.leave_title'),
                message: this.$t('phu_production.configuration.leave_message'),
                textCancel: this.$t('phu_production.configuration.keep_editing'),
                textSave: this.$t('phu_production.configuration.leave'),
                callSave: () => this.close()
            })
        },
        close() {
            this.$store.commit('phuProduction/setConfiguration', false)
        },
        changeCount({ key, count }) {
            const group = this.groups.find((item) => item.key === key)
            if (!group || count < 0 || count > group.max) {
                return
            }
            group.count = count
        },
        phusOf(workstationId) {
            return this.phus.filter((phu) => phu.workstation_id === workstationId).sort((a, b) => a.sort_order - b.sort_order)
        },
        renamePhu({ phuId, name }) {
            const phu = this.phus.find((item) => item.id === phuId)
            phu.name = name
        },
        movePhu({ phuId, direction }) {
            const phu = this.phus.find((item) => item.id === phuId)
            const siblings = this.phusOf(phu.workstation_id)
            const index = siblings.findIndex((item) => item.id === phuId)
            const target = index + direction
            if (target < 0 || target >= siblings.length) {
                return
            }
            const reordered = siblings.slice()
            const moved = reordered.splice(index, 1)[0]
            reordered.splice(target, 0, moved)
            reordered.forEach((item, position) => {
                item.sort_order = position + 1
            })
        },
        confirmRemovePhu(phu) {
            this.$popup.delete({
                message: this.$t('phu_production.configuration.delete_confirm', { name: phu.name }),
                textSave: this.$t('phu_production.configuration.delete'),
                textCancel: this.$t('phu_production.common.cancel'),
                callSave: () => {
                    this.phus = this.phus.filter((item) => item.id !== phu.id)
                }
            })
        },
        addPhu(workstationId) {
            if (this.layouts.length === 0) {
                return
            }
            const siblings = this.phusOf(workstationId)
            this.phus.push({
                id: `phu-${Date.now()}`,
                workstation_id: workstationId,
                name: this.$t('phu_production.configuration.phu_default_name', { number: siblings.length + 1 }),
                layout_option_id: null,
                rows: 0,
                columns: 0,
                sort_order: siblings.length + 1,
                fixed_layout: false,
                choosing_layout: true,
                slots: []
            })
        },
        editLayout(phuId) {
            const phu = this.phus.find((item) => item.id === phuId)
            if (!phu || phu.fixed_layout) {
                return
            }
            phu.choosing_layout = true
        },
        changeLayout({ phuId, layout }) {
            const phu = this.phus.find((item) => item.id === phuId)
            const selected = this.layouts.find((item) => Number(item.id) === Number(layout && layout.id))
            const size = layoutSize(selected || layout)
            if (!phu || phu.fixed_layout || size.rows < 1 || size.columns < 1) {
                return
            }
            const total = size.rows * size.columns
            const sameLayout = Number(phu.layout_option_id) === Number((selected || layout).id) && phu.slots.length === total
            if (sameLayout) {
                phu.rows = size.rows
                phu.columns = size.columns
                phu.choosing_layout = false
                return
            }
            const blocked = phu.slots.some((slot) => slot.position > total && (slot.product_id || slot.units || slot.batch_id))
            if (blocked) {
                this.$snackbar.error({ message: this.$t('phu_production.configuration.layout_blocked'), closeable: true })
                return
            }
            const slots = []
            for (let position = 1; position <= total; position += 1) {
                const current = phu.slots.find((slot) => slot.position === position)
                slots.push(current || { id: `${phu.id}-${position}`, position, product_id: null, ...emptyRuntime() })
            }
            phu.layout_option_id = (selected || layout).id
            phu.rows = size.rows
            phu.columns = size.columns
            phu.slots = slots
            phu.choosing_layout = false
        },
        findSlot(phuId, slotId) {
            const phu = this.phus.find((item) => item.id === phuId)
            return phu.slots.find((slot) => slot.id === slotId)
        },
        assignSlot(phuId, slotId, productId) {
            const slot = this.findSlot(phuId, slotId)
            slot.product_id = productId
            if (!productId) {
                Object.assign(slot, emptyRuntime())
                return
            }
            this.notifyMinSlots(phuId, productId)
        },
        notifyMinSlots(phuId, productId) {
            const phu = this.phus.find((item) => item.id === phuId)
            const underAssigned = getUnderAssignedProducts(this.phusOf(phu.workstation_id), this.minSlots)
            const count = Number(underAssigned[String(productId)]) || 0
            const product = this.products[productId]
            if (!count || !product) {
                return
            }
            this.$snackbar.error({
                message: this.$t('phu_production.configuration.min_slots', {
                    product: product.name,
                    min: this.minSlots,
                    count: count
                }),
                closeable: true
            })
        },
        selectSlot({ phu, slot }) {
            const phuId = phu.id
            const slotId = slot.id
            this.$popup.phuProduct({
                list: this.productList.filter((product) => product.status),
                selectedEl: slot.product_id || null,
                callSave: (popup) => {
                    this.assignSlot(phuId, slotId, popup.selectedEl || null)
                }
            })
        },
        clearSlot({ phuId, slotId }) {
            this.assignSlot(phuId, slotId, null)
        },
        toggleProduct(productId) {
            const product = this.products[productId]
            product.status = !product.status
        },
        setContainer({ productId, field, value }) {
            const activeKey = {
                container: 'active_container',
                package: 'active_package',
                drop: 'active_drop',
                unit: 'active_unit'
            }[field]
            const product = this.products[productId]
            if (!product || !activeKey) {
                return
            }
            product[activeKey] = value
        },
        hasInvalidSlots() {
            return this.workstations.some((workstation) => Object.keys(getUnderAssignedProducts(this.phusOf(workstation.id), this.minSlots)).length > 0)
        },
        buildEquipment(key, current) {
            const group = this.groups.find((item) => item.key === key)
            const count = group ? group.count : 0
            const list = []

            for (let index = 1; index <= count; index += 1) {
                const existing = current.find((item) => Number(item.id) === index)
                list.push(existing ? { ...existing, id: index } : { id: index, status: EQUIPMENT_STATUS.AVAILABLE })
            }
            return list
        },
        buildWorkstations() {
            return this.workstations.reduce((acc, workstation) => {
                const phus = this.phusOf(workstation.id)
                if (phus.length === 0 && !workstation.server_id) {
                    return acc
                }
                acc.push({
                    id: workstation.server_id,
                    type: workstation.type,
                    name: workstation.name,
                    phus: phus.map((phu) => ({
                        id: typeof phu.id === 'number' ? phu.id : null,
                        name: phu.name,
                        layout_option_id: phu.layout_option_id,
                        fixed_layout: phu.fixed_layout,
                        slots: [...phu.slots]
                            .sort((a, b) => a.position - b.position)
                            .map((slot) => ({ position: slot.position, reference_id: slot.product_id || null }))
                    }))
                })
                return acc
            }, [])
        },
        currentEquipment(key) {
            if (key === 'broiler') {
                return this.$store.getters['phuProduction/getBroilerEquipment'].map((item) => ({ id: item.number, status: item.status }))
            }
            return this.$store.getters['phuProduction/getFryerEquipment'] || []
        },
        buildPayload() {
            return {
                equipment: this.groups.reduce((acc, group) => {
                    acc[group.key] = this.buildEquipment(group.key, this.currentEquipment(group.key))
                    return acc
                }, {}),
                workstations: this.buildWorkstations()
            }
        },
        save() {
            if (this.phus.some((phu) => !phu.layout_option_id)) {
                this.$snackbar.error({ message: this.$t('phu_production.configuration.select_layout'), closeable: true })
                this.scrollTo('holding')
                return
            }
            if (this.hasInvalidSlots()) {
                this.$snackbar.error({ message: this.$t('phu_production.configuration.validation_error'), closeable: true })
                this.scrollTo('holding')
                return
            }

            const payload = this.productList.map((product) => {
                const item = { id: product.id, status: product.status ? 1 : 0 }
                const selected = [
                    ['active_container', 'container'],
                    ['active_package', 'package'],
                    ['active_drop', 'drop'],
                    ['active_unit', 'unit']
                ]
                selected.forEach(([activeKey, field]) => {
                    if (product[activeKey]) {
                        item[field] = product[activeKey]
                    }
                })
                return item
            })
            this.$overlay.loading()

            Promise.all([
                this.$store.dispatch('production/updateConfiguration', { products: JSON.stringify(payload) }),
                this.$store.dispatch('phuProduction/saveConfiguration', this.buildPayload())
            ])
                .then(() => {
                    this.$snackbar.info({ message: this.$t('phu_production.configuration.saved'), closeable: true })
                    this.$store.dispatch('phuProduction/loadData')
                    this.close()
                })
                .catch(() => {
                    this.$snackbar.error({ message: this.$t('phu_production.errors.sync'), closeable: true })
                })
                .then(() => this.$overlay.hide())
        }
    },
    created() {
        this.products = cloneDeep(this.$store.getters['phuProduction/getProducts'])
        this.phus = cloneDeep(this.$store.getters['phuProduction/getAllPhus']).map((phu) => ({ ...phu, choosing_layout: false }))
        this.groups = this.$store.getters['phuProduction/getEquipmentCatalog'].map((item) => ({
            key: item.type,
            name: item.name,
            count: this.currentEquipment(item.type).length,
            min: 1,
            max: item.max
        }))
        this.savedSnapshot = this.configurationSnapshot()
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-config {
    display: grid;
    grid-template-columns: 272px minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);
    gap: $phu-gap;
    height: 100%;
    min-height: 0;
    padding: 8px;
    overflow: hidden;
    background-color: $phu-board;

    &__main {
        @include display-flex();
        @include flex-direction(column);
        gap: 16px;
        min-width: 0;
        min-height: 0;
        padding: 8px;
        overflow-y: auto;
    }

    &__actions {
        @include display-flex();
        @include justify-content(center);
        gap: 16px;
        padding: 8px 0 16px;
    }

    &__cancel,
    &__save {
        min-width: 173px;
        min-height: 40px;
        padding: 8px 16px;
        border: none;
        border-radius: $phu-radius-s;
        font-family: $text-bold;
        font-size: 1.25rem;
        line-height: 18px;
        cursor: pointer;
    }

    &__cancel {
        background-color: $phu-button;
        color: $phu-primary;
    }

    &__save {
        background-color: $phu-primary;
        color: $color-white;
    }
}
</style>
